import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Pencil, Trash2, Search, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const MaterialTypesTab = ({ api, materialTypes, fetchMaterialTypes }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState({
    name: '',
    default_unit: 'tonne',
    is_active: true
  });

  const resetForm = () => {
    setForm({ name: '', default_unit: 'tonne', is_active: true });
    setEditingType(null);
  };

  const handleOpenDialog = (type = null) => {
    if (type) {
      setEditingType(type);
      setForm({
        name: type.name,
        default_unit: type.default_unit || 'tonne',
        is_active: type.is_active
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await api.put(`/material-types/${editingType.id}`, form);
        toast.success('Material type updated');
      } else {
        await api.post('/material-types', form);
        toast.success('Material type created');
      }
      setDialogOpen(false);
      resetForm();
      fetchMaterialTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (type) => {
    if (!confirm(`Are you sure you want to delete "${type.name}"?`)) return;
    try {
      await api.delete(`/material-types/${type.id}`);
      toast.success('Material type deleted');
      fetchMaterialTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete material type');
    }
  };

  const toggleActive = async (type) => {
    try {
      await api.put(`/material-types/${type.id}`, { is_active: !type.is_active });
      toast.success(`Material type ${type.is_active ? 'deactivated' : 'activated'}`);
      fetchMaterialTypes();
    } catch (error) {
      toast.error('Failed to update material type');
    }
  };

  const filteredTypes = materialTypes.filter(mt => 
    mt.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Card className="card-industrial">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading">Material Types</CardTitle>
              <CardDescription>
                Manage material types for disposal and pickup tracking (e.g., Sand, Concrete, Clean Fill)
              </CardDescription>
            </div>
            <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-material-type-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Material Type
            </Button>
          </div>
          <div className="relative max-w-sm mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="material-type-search"
              placeholder="Search material types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Default Unit</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTypes.map(type => (
                <TableRow key={type.id}>
                  <TableCell className="font-medium">{type.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{type.default_unit}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={type.is_active ? "default" : "secondary"}
                      className={type.is_active ? "bg-green-500/20 text-green-400 hover:bg-green-500/30" : ""}
                    >
                      {type.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenDialog(type)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleActive(type)}>
                          {type.is_active ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => handleDelete(type)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTypes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No matching material types found' : 'No material types configured. Add your first one!'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Material Type Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingType ? 'Edit Material Type' : 'Add Material Type'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="material-form">
            <div>
              <Label htmlFor="material-name">Name</Label>
              <Input
                id="material-name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Sand, Concrete, Clean Fill"
                required
              />
            </div>
            <div>
              <Label htmlFor="material-unit">Default Unit</Label>
              <Select 
                value={form.default_unit} 
                onValueChange={(v) => setForm(prev => ({ ...prev, default_unit: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tonne">Tonne</SelectItem>
                  <SelectItem value="m3">Cubic Metre (m³)</SelectItem>
                  <SelectItem value="load">Load</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="material-active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="material-active">Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt">
                {editingType ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MaterialTypesTab;
